import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const HEARTBEAT_MS = 30000; // 30s
const POLL_MS = 20000; // 20s

// Deterministic hue from string
const hue = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

const initials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 30) return "activo ahora";
  if (s < 60) return `hace ${s}s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)}m`;
  return `hace ${Math.floor(s / 3600)}h`;
};

export default function PresenceIndicator() {
  const { user } = useAuth();
  const [online, setOnline] = useState([]);

  const heartbeat = useCallback(async () => {
    try {
      await api.post("/presence/heartbeat");
    } catch (e) {
      console.warn("PresenceIndicator.heartbeat failed:", e);
    }
  }, []);

  const loadOnline = useCallback(async () => {
    try {
      const { data } = await api.get("/presence/online");
      setOnline(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("PresenceIndicator.loadOnline failed:", e);
    }
  }, []);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "dispatcher")) return;
    heartbeat();
    loadOnline();
    const hb = setInterval(heartbeat, HEARTBEAT_MS);
    const pl = setInterval(loadOnline, POLL_MS);
    const onFocus = () => { heartbeat(); loadOnline(); };
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(hb);
      clearInterval(pl);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, heartbeat, loadOnline]);

  if (!user || (user.role !== "admin" && user.role !== "dispatcher")) return null;
  if (online.length === 0) return null;

  // put self first, then dispatchers, then admins
  const sorted = [...online].sort((a, b) => {
    if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <TooltipProvider delayDuration={100}>
      <div
        className="flex items-center -space-x-1.5"
        data-testid="presence-indicator"
      >
        {sorted.slice(0, 8).map((u) => {
          const h = hue(u.id || u.email);
          const bg = `hsl(${h}, 65%, 42%)`;
          return (
            <Tooltip key={u.id}>
              <TooltipTrigger asChild>
                <div
                  data-testid={`presence-avatar-${u.id}`}
                  className={`relative w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold text-white ring-2 ring-card cursor-default select-none transition-transform hover:scale-110 hover:z-10 ${
                    u.is_self ? "ring-primary/70" : ""
                  }`}
                  style={{ background: bg }}
                >
                  {initials(u.name || u.email)}
                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-1 ring-card" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <div className="font-semibold">
                  {u.name || u.email}
                  {u.is_self && <span className="text-muted-foreground font-normal"> (tú)</span>}
                </div>
                <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono">
                  {u.role} · {timeAgo(u.last_seen)}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
        {sorted.length > 8 && (
          <div className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold bg-secondary text-muted-foreground ring-2 ring-card">
            +{sorted.length - 8}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
